import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import { getCurrencySymbol, getDialCode } from '@/constants/countries';
import { FALABELLA_ENABLED_COUNTRIES, isFalabellaEnabledCountry } from '@/constants/falabella';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { RootState } from '@/redux/store';

import type { AddressDetails } from '@/components/address-autocomplete';
import type { IFalabellaLocalConfig, IFalabellaPluginDetail } from '@/types/plugins/falabella';
import type { Countries } from '@/utils/common';
import type { MuiTelInputInfo } from 'mui-tel-input';

interface UseFalabellaConfigProps {
  pluginId: string;
  clientId: string;
  isOpen: boolean;
}

const configSchema = yup.object().shape({
  autoCreateOrder: yup.boolean().required(),

  // 1. Información de origen / contacto de tienda
  overrideStoreInfo: yup.boolean().default(false),
  storeName: yup.string().when('overrideStoreInfo', {
    is: true,
    then: (schema) => schema.trim().required('El nombre de la tienda es obligatorio'),
    otherwise: (schema) => schema.optional(),
  }),
  storeEmail: yup.string().when('overrideStoreInfo', {
    is: true,
    then: (schema) =>
      schema.trim().email('El email no es válido').required('El email de la tienda es obligatorio'),
    otherwise: (schema) => schema.optional(),
  }),
  storePhone: yup.string().when('overrideStoreInfo', {
    is: true,
    then: (schema) =>
      schema
        .trim()
        .required('El teléfono de la tienda es obligatorio')
        .test('phone-valid', 'Ingresa un número de teléfono válido', (val) => {
          if (!val) return false;
          const digits = val.replace(/\D/g, '');
          return digits.length >= 7;
        }),
    otherwise: (schema) => schema.optional(),
  }),

  // 2. Toggle origen marketplace vs dirección propia
  useCustomAddress: yup.boolean().default(false),
  collectAddress: yup
    .object()
    .shape({
      address: yup.string().default(''),
      country: yup.string().default(''),
      latitude: yup.number().default(0),
      longitude: yup.number().default(0),
      references: yup.string().optional().default(''),
      zipCode: yup.string().optional().default(''),
    })
    .test('custom-address-required', function (value) {
      const parent = this.parent as { useCustomAddress?: boolean };
      if (!parent?.useCustomAddress) return true;
      if (!value?.address?.trim()) {
        return this.createError({
          path: 'collectAddress.address',
          message: 'La dirección es obligatoria',
        });
      }
      return true;
    }),

  // 3. Seguro adicional condicional
  additionalInsurance: yup.boolean().default(false),
  additionalInsuranceLimit: yup.string().when('additionalInsurance', {
    is: true,
    then: (schema) =>
      schema
        .required('Ingresa un monto mayor a 0')
        .test('is-positive', 'Ingresa un monto mayor a 0', (val) => {
          if (!val) return false;
          const num = Number(val);
          return !isNaN(num) && num > 0;
        }),
    otherwise: (schema) => schema.optional(),
  }),
});

type ConfigFormValues = yup.InferType<typeof configSchema>;

const normalizePhone = (phone: string, dialCode: string) => {
  if (!phone) return '';
  if (phone.startsWith('+')) return phone;
  const digits = phone.replace(/\D/g, '');
  return `${dialCode} ${digits}`;
};

const useFalabellaConfig = (props: UseFalabellaConfigProps) => {
  const { pluginId, clientId, isOpen } = props;
  const { t } = useTranslation('falabella');
  const profileCountry = useSelector((state: RootState) => state.parcel.profile.data?.country);
  const userCountry = isFalabellaEnabledCountry(profileCountry)
    ? profileCountry!
    : FALABELLA_ENABLED_COUNTRIES[0];
  const currencySymbol = getCurrencySymbol(userCountry as Countries);
  const dialCode = getDialCode(userCountry as Countries);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [phoneError, setPhoneError] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ConfigFormValues>({
    resolver: yupResolver(configSchema),
    defaultValues: {
      autoCreateOrder: true,
      overrideStoreInfo: false,
      storeName: '',
      storeEmail: '',
      storePhone: '',
      useCustomAddress: false,
      collectAddress: {
        address: '',
        country: userCountry,
        latitude: 0,
        longitude: 0,
        references: '',
        zipCode: '',
      },
      additionalInsurance: false,
      additionalInsuranceLimit: '',
    },
  });

  const useCustomAddress = watch('useCustomAddress');
  const overrideStoreInfo = watch('overrideStoreInfo');
  const additionalInsurance = watch('additionalInsurance');
  const autoCreateOrder = watch('autoCreateOrder');

  const handleSelectAddress = (details: AddressDetails) => {
    setValue('collectAddress.address', details.address, { shouldValidate: true });
    setValue('collectAddress.latitude', details.latitude, { shouldValidate: true });
    setValue('collectAddress.longitude', details.longitude, { shouldValidate: true });
    setValue('collectAddress.zipCode', details.zipCode, { shouldValidate: true });
  };

  const handlePhoneChange = (value: string, info: MuiTelInputInfo) => {
    setValue('storePhone', value, { shouldValidate: true });
    const digits = (info?.nationalNumber ?? '').replace(/\D/g, '');
    setPhoneError(digits.length > 0 && digits.length < 7);
  };

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setPhoneError(false);
    try {
      const http = initializeHttpProvider();
      const response = await http.falabella.getPluginDetails({ clientId, pluginId });

      if (response && 'data' in response && response.success && !Array.isArray(response.data)) {
        const detail = response.data as IFalabellaPluginDetail;
        const { localConfig } = detail;

        if (localConfig) {
          const hasCustomAddress = Boolean(
            localConfig.useCollectAddress ?? Boolean(localConfig.collectAddress?.address),
          );
          const hasStoreInfo = Boolean(
            localConfig.storeName || localConfig.storeEmail || localConfig.storePhone,
          );
          const hasInsurance = Boolean(localConfig.additionalInsurance);

          reset({
            autoCreateOrder: localConfig.autoCreateOrder ?? true,
            overrideStoreInfo: hasStoreInfo,
            storeName: localConfig.storeName || '',
            storeEmail: localConfig.storeEmail || '',
            storePhone: normalizePhone(localConfig.storePhone || '', dialCode),
            useCustomAddress: hasCustomAddress,
            collectAddress: {
              address: localConfig.collectAddress?.address || '',
              country: localConfig.collectAddress?.country || userCountry,
              latitude: localConfig.collectAddress?.latitude || 0,
              longitude: localConfig.collectAddress?.longitude || 0,
              references: localConfig.collectAddress?.references || '',
              zipCode: localConfig.collectAddress?.zipCode || '',
            },
            additionalInsurance: hasInsurance,
            additionalInsuranceLimit: localConfig.additionalInsuranceLimit
              ? String(localConfig.additionalInsuranceLimit)
              : '',
          });
        }
      }
    } catch {
      toast.error(t('config.loadError'));
    } finally {
      setLoading(false);
    }
  }, [clientId, dialCode, pluginId, reset, t, userCountry]);

  useEffect(() => {
    let isMounted = true;
    if (isOpen && pluginId) {
      void Promise.resolve().then(() => {
        if (isMounted) {
          void fetchConfig();
        }
      });
    }
    return () => {
      isMounted = false;
    };
  }, [isOpen, pluginId, fetchConfig]);

  const saveConfig = async (data: ConfigFormValues) => {
    if (data.overrideStoreInfo) {
      const digits = (data.storePhone || '').replace(/\D/g, '');
      if (!data.storePhone?.trim()) {
        toast.error(t('config.phoneRequired', 'El teléfono de la tienda es obligatorio'));
        return false;
      }
      if (phoneError || digits.length < 7) {
        toast.error(t('config.phoneInvalid'));
        return false;
      }
    }

    setSaving(true);
    try {
      const http = initializeHttpProvider();
      const payload: IFalabellaLocalConfig = {
        autoCreateOrder: data.autoCreateOrder,
        useCollectAddress: Boolean(data.useCustomAddress),
        collectAddress: data.useCustomAddress
          ? {
              address: data.collectAddress?.address || '',
              country: data.collectAddress?.country || userCountry,
              latitude: data.collectAddress?.latitude || 0,
              longitude: data.collectAddress?.longitude || 0,
              references: data.collectAddress?.references || '',
              zipCode: data.collectAddress?.zipCode || '',
            }
          : undefined,
        storeName: data.overrideStoreInfo ? data.storeName?.trim() || '' : '',
        storeEmail: data.overrideStoreInfo ? data.storeEmail?.trim() || '' : '',
        storePhone: data.overrideStoreInfo ? data.storePhone?.trim() || '' : '',
        additionalInsurance: Boolean(data.additionalInsurance),
        additionalInsuranceLimit:
          data.additionalInsurance && data.additionalInsuranceLimit
            ? Number(data.additionalInsuranceLimit)
            : null,
      };

      const response = await http.falabella.updatePlugin({
        clientId,
        pluginId,
        data: {
          local_config: payload,
        },
      });

      if (
        !response ||
        ('success' in response && !response.success) ||
        ('code' in response && typeof response.code === 'number' && response.code >= 400)
      ) {
        throw new Error(
          response && 'message' in response && typeof response.message === 'string'
            ? response.message
            : t('config.saveError'),
        );
      }

      toast.success(t('config.saveSuccess'));
      return true;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t('config.saveError');
      toast.error(errorMessage);
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    loading,
    saving,
    userCountry,
    currencySymbol,
    control,
    errors,
    useCustomAddress,
    overrideStoreInfo,
    additionalInsurance,
    autoCreateOrder,
    phoneError,
    handlePhoneChange,
    handleSelectAddress,
    handleSubmit,
    saveConfig,
    setValue,
    watch,
  };
};

export default useFalabellaConfig;
